/**
 * Readable message out of whatever an API call rejected with.
 *
 * The shared axios client (`src/api/axios.js`) does not reject with an axios
 * error. `handleError` throws its own value per status: for 400/417 it throws
 * the raw *response body*, for others a status text or message string. So a
 * 400 has no `.response`/`.message` at all and the usual
 * `err?.response?.data?.error` reads undefined — which is why validation
 * errors surfaced to users as "undefined".
 *
 * Handles, in order: a plain string, a body/axios error carrying
 * error/message/detail/title, a list of field errors, and finally a JSON dump
 * so a message is never silently dropped.
 */

const MESSAGE_KEYS = ["error", "message", "detail", "title", "errorMessage"];

const firstString = (source) => {
  if (!source || typeof source !== "object") return null;
  for (const key of MESSAGE_KEYS) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
};

/** Field-level validation errors: ["a", "b"] or [{message}|{error}|{defaultMessage}]. */
const fromErrorList = (source) => {
  const list = source?.errors || source?.fieldErrors || source?.violations;
  if (!Array.isArray(list) || list.length === 0) return null;

  const messages = list
    .map((item) => {
      if (typeof item === "string") return item.trim();
      const text = firstString(item) || item?.defaultMessage;
      const field = item?.field || item?.fieldName;
      if (!text) return null;
      return field ? `${field}: ${text}` : text;
    })
    .filter(Boolean);

  return messages.length > 0 ? messages.join(", ") : null;
};

export function getApiErrorMessage(error, fallback = "Unknown error") {
  if (typeof error === "string" && error.trim()) return error.trim();
  if (!error || typeof error !== "object") return fallback;

  // An axios error still has the body one level down; the shared client's
  // 400 path throws that body directly, so check both.
  const body = error.response?.data;

  return (
    fromErrorList(body) ||
    firstString(body) ||
    fromErrorList(error) ||
    firstString(error) ||
    (body && typeof body === "string" && body.trim()) ||
    safeStringify(body) ||
    safeStringify(error) ||
    fallback
  );
}

function safeStringify(value) {
  if (!value || typeof value !== "object") return null;
  try {
    const json = JSON.stringify(value);
    return json && json !== "{}" ? json : null;
  } catch {
    return null;
  }
}
